/**
 * CrmActivitiesCalendar - Página do Calendário de Atividades do CRM
 * Visão completa de agendamentos estilo Google Calendar
 *
 * @version 1.3.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial da página do calendário
 * RELEASE: 2026-02-14 v1.1.0 - Adicionados filtros (Período, Workspace, Vendedor, Responsável, Status)
 * RELEASE: 2026-02-14 v1.2.0 - Substituída sidebar por KPIs de agendamento e lista de Event Types
 * RELEASE: 2026-05-16 v1.3.0 - Altura do card do calendário sincronizada com a sidebar via ResizeObserver,
 *                              evitando que o FullCalendar extrapole as bordas do container em telas lg+.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { X, CalendarDays, List, RefreshCw, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ActivitiesFullCalendar } from '@/components/crm/activities/calendar';
import { CalendarToolbar, type CalendarView } from '@/components/crm/activities/calendar/CalendarToolbar';
import { SchedulingKpiCard } from '@/components/crm/activities/SchedulingKpiCard';
import { SchedulingTypeBreakdownCard } from '@/components/crm/activities/SchedulingTypeBreakdownCard';
import { SchedulingEventTypesCompact } from '@/components/crm/activities/SchedulingEventTypesCompact';
import { ActivitiesFilters, type ActivityFilters } from '@/components/crm/activities/ActivitiesFilters';
import { SchedulingListView } from '@/components/crm/activities/SchedulingListView';
import { LinkBookingToDealDialog } from '@/components/crm/activities/LinkBookingToDealDialog';
import { InternalBookingDialog } from '@/components/crm/activities/InternalBookingDialog';
import { CalendarEventModal } from '@/components/crm/activities/calendar/CalendarEventModal';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { useSchedulingKpi } from '@/hooks/useSchedulingKpi';
import { useCalendarActivities } from '@/hooks/useCalendarActivities';
import { useSchedulingRealtime } from '@/hooks/useSchedulingRealtime';
import { useSchedulingListActivities } from '@/hooks/useSchedulingListActivities';
import { useStandaloneBookings } from '@/hooks/useStandaloneBookings';
import { useAuth } from '@/hooks/useAuth';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, subDays, startOfDay, endOfDay, subMonths, addMonths, addWeeks, subWeeks, addDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { PeriodPreset, PeriodFilter } from '@/hooks/useCrmOverviewFilters';

/**
 * Calcula as datas de início e fim baseado no preset selecionado
 */
function calculatePeriodDates(preset: PeriodPreset): { startDate: Date; endDate: Date } {
  const today = new Date();
  
  switch (preset) {
    case 'today':
      return { startDate: startOfDay(today), endDate: endOfDay(today) };
    case 'yesterday':
      const yesterday = subDays(today, 1);
      return { startDate: startOfDay(yesterday), endDate: endOfDay(yesterday) };
    case '7days':
      return { startDate: startOfDay(subDays(today, 6)), endDate: endOfDay(today) };
    case '15days':
      return { startDate: startOfDay(subDays(today, 14)), endDate: endOfDay(today) };
    case 'month':
      return { startDate: startOfMonth(today), endDate: endOfMonth(today) };
    case 'lastMonth':
      const lastMonth = subMonths(today, 1);
      return { startDate: startOfMonth(lastMonth), endDate: endOfMonth(lastMonth) };
    default:
      return { startDate: startOfMonth(today), endDate: endOfMonth(today) };
  }
}

/**
 * Página principal do Calendário de Atividades
 */
export default function CrmActivitiesCalendar() {
  const { profile } = useAuth();

  // Ativa Realtime para sincronização automática de agendamentos
  useSchedulingRealtime();
  const SCHEDULING_VIEW_KEY = 'schedulingViewMode';

  // Estado do modo de visualização (calendário ou lista) - persistido no localStorage
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>(() => {
    const saved = localStorage.getItem(SCHEDULING_VIEW_KEY);
    return saved === 'list' ? 'list' : 'calendar';
  });

  // Handler que salva no localStorage ao trocar modo de visualização
  const handleViewModeChange = (val: string) => {
    if (val) {
      const mode = val as 'calendar' | 'list';
      setViewMode(mode);
      localStorage.setItem(SCHEDULING_VIEW_KEY, mode);
    }
  };

  // ========================================================================
  // Estado da toolbar de navegação do calendário (compartilhado entre views)
  // ========================================================================
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<CalendarView>('dayGridMonth');

  /** Título formatado do período baseado na visão e data atuais */
  const calendarTitle = useMemo(() => {
    switch (calendarView) {
      case 'dayGridMonth':
        return format(calendarDate, "MMMM 'de' yyyy", { locale: ptBR });
      case 'timeGridWeek':
        return format(calendarDate, "'Semana de' dd 'de' MMMM", { locale: ptBR });
      case 'timeGridDay':
        return format(calendarDate, "EEEE, dd 'de' MMMM", { locale: ptBR });
      default:
        return format(calendarDate, "MMMM 'de' yyyy", { locale: ptBR });
    }
  }, [calendarDate, calendarView]);

  /**
   * Helper: calcula startDate/endDate baseado na data e visão do calendário
   * Usado para sincronizar toolbar → filtros de busca de dados
   */
  const getCalendarPeriod = useCallback((date: Date, view: CalendarView) => {
    switch (view) {
      case 'dayGridMonth':
        return { startDate: startOfMonth(date), endDate: endOfMonth(date) };
      case 'timeGridWeek':
        return { startDate: startOfWeek(date, { locale: ptBR }), endDate: endOfWeek(date, { locale: ptBR }) };
      case 'timeGridDay':
        return { startDate: startOfDay(date), endDate: endOfDay(date) };
      default:
        return { startDate: startOfMonth(date), endDate: endOfMonth(date) };
    }
  }, []);

  /**
   * Atualiza filters.period a partir da toolbar de navegação
   * Usa preset 'custom' para refletir período calculado pela toolbar
   */
  const syncFiltersFromToolbar = useCallback((date: Date, view: CalendarView) => {
    const { startDate, endDate } = getCalendarPeriod(date, view);
    setFilters(prev => ({
      ...prev,
      period: { preset: 'custom' as PeriodPreset, startDate, endDate },
    }));
  }, [getCalendarPeriod]);

  /** Navegar para período anterior */
  const handleCalendarPrev = useCallback(() => {
    setCalendarDate(prev => {
      let newDate: Date;
      if (calendarView === 'dayGridMonth') newDate = subMonths(prev, 1);
      else if (calendarView === 'timeGridWeek') newDate = subWeeks(prev, 1);
      else newDate = subDays(prev, 1);
      syncFiltersFromToolbar(newDate, calendarView);
      return newDate;
    });
  }, [calendarView, syncFiltersFromToolbar]);

  /** Navegar para próximo período */
  const handleCalendarNext = useCallback(() => {
    setCalendarDate(prev => {
      let newDate: Date;
      if (calendarView === 'dayGridMonth') newDate = addMonths(prev, 1);
      else if (calendarView === 'timeGridWeek') newDate = addWeeks(prev, 1);
      else newDate = addDays(prev, 1);
      syncFiltersFromToolbar(newDate, calendarView);
      return newDate;
    });
  }, [calendarView, syncFiltersFromToolbar]);

  /** Navegar para hoje */
  const handleCalendarToday = useCallback(() => {
    const today = new Date();
    setCalendarDate(today);
    syncFiltersFromToolbar(today, calendarView);
  }, [calendarView, syncFiltersFromToolbar]);

  /** Mudar visão (mês/semana/dia) — recalcula período para a data atual */
  const handleCalendarViewChange = useCallback((view: CalendarView) => {
    setCalendarView(view);
    syncFiltersFromToolbar(calendarDate, view);
  }, [calendarDate, syncFiltersFromToolbar]);
  const [filters, setFilters] = useState<ActivityFilters>(() => {
    const today = new Date();
    return {
      period: {
        preset: 'month' as PeriodPreset,
        startDate: startOfMonth(today),
        endDate: endOfMonth(today),
      },
      workspaceId: null,
      sellerId: null,
      performedByUserId: null,
      status: 'all' as const,
      schedulingType: null,
    };
  });

  // Verifica se algum filtro difere do padrão
  const hasActiveFilters =
    filters.period.preset !== 'month' ||
    filters.workspaceId !== null ||
    filters.sellerId !== null ||
    filters.performedByUserId !== null ||
    filters.status !== 'all' ||
    filters.schedulingType !== null;

  // Handler para limpar todos os filtros
  const handleClearFilters = () => {
    const today = new Date();
    setFilters({
      period: {
        preset: 'month' as PeriodPreset,
        startDate: startOfMonth(today),
        endDate: endOfMonth(today),
      },
      workspaceId: null,
      sellerId: null,
      performedByUserId: null,
      status: 'all' as const,
      schedulingType: null,
    });
  };

  // Estado do período para KPIs (sincronizado com filtros)
  const periodFilter = filters.period;

  // Estado do modal de negócio
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);

  // Estado do modal de detalhes da atividade (visualização lista)
  const [selectedListEvent, setSelectedListEvent] = useState<any>(null);
  const [isListEventModalOpen, setIsListEventModalOpen] = useState(false);

  // Estado do dialog de vincular booking a deal
  const [linkBookingId, setLinkBookingId] = useState<string | null>(null);
  const [linkGuestName, setLinkGuestName] = useState<string>('');
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

  // Estado do dialog de novo agendamento interno
  const [isInternalBookingOpen, setIsInternalBookingOpen] = useState(false);

  // ========================================================================
  // Espelhamento de altura: o card do calendário (esquerda) deve ter
  // exatamente a mesma altura da sidebar (KPIs + Breakdown + Event Types).
  // Usamos ResizeObserver pois os cards carregam de forma assíncrona e
  // suas alturas mudam após o fetch dos KPIs.
  // ========================================================================
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [sidebarHeight, setSidebarHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    // Atualiza altura inicial e observa mudanças (skeleton -> conteúdo real)
    const update = () => setSidebarHeight(el.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [viewMode]);

  // Hook para buscar KPIs de agendamento
  const { kpiData, isLoading: isLoadingKpi, refetch: refetchKpi } = useSchedulingKpi({
    startDate: periodFilter.startDate,
    endDate: periodFilter.endDate,
    workspaceId: filters.workspaceId,
    sellerId: filters.sellerId,
    performedByUserId: filters.performedByUserId,
  });

  // Hook para buscar atividades do calendário (usado no modo calendário + mutations para lista)
  const {
    events,
    isLoading: isLoadingActivities,
    refetch: refetchActivities,
    completeActivity,
    reopenActivity,
    cancelActivity,
  } = useCalendarActivities({
    startDate: periodFilter.startDate,
    endDate: periodFilter.endDate,
    typeFilter: filters.schedulingType === 'online' ? 'meeting' : (filters.schedulingType ?? null),
    workspaceId: filters.workspaceId,
    sellerId: filters.sellerId,
    performedByUserId: filters.performedByUserId,
    status: filters.status,
    eventTypeId: filters.eventTypeId ?? null,
  });

  // Hook para buscar agendamentos com scroll infinito (usado apenas no modo lista)
  const {
    activities: listActivities,
    isLoading: isLoadingList,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch: refetchList,
  } = useSchedulingListActivities({
    startDate: periodFilter.startDate,
    endDate: periodFilter.endDate,
    workspaceId: filters.workspaceId,
    sellerId: filters.sellerId,
    performedByUserId: filters.performedByUserId,
    status: filters.status,
    schedulingType: filters.schedulingType ?? null,
    eventTypeId: filters.eventTypeId ?? null,
  });

  // Hook para buscar bookings standalone (sem deal) - v1.3.0
  const {
    bookings: standaloneBookings,
    isLoading: isLoadingStandalone,
    refetch: refetchStandalone,
    linkToDeal,
  } = useStandaloneBookings({
    startDate: periodFilter.startDate,
    endDate: periodFilter.endDate,
    status: filters.status,
    performedByUserId: filters.performedByUserId,
    eventTypeId: filters.eventTypeId ?? null,
    schedulingType: filters.schedulingType ?? null,
  });

  // Merge das atividades CRM + bookings standalone para a lista unificada
  const mergedListActivities = useMemo(() => {
    const all = [...listActivities, ...standaloneBookings];
    // Ordena por data de agendamento
    return all.sort((a, b) =>
      new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()
    );
  }, [listActivities, standaloneBookings]);

  // Converte bookings standalone para formato EventInput do FullCalendar (modo calendário)
  const mergedCalendarEvents = useMemo(() => {
    const standaloneEvents = standaloneBookings.map((booking) => {
      const startDate = new Date(booking.scheduled_at);
      const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
      const eventColor = (booking as any).event_type_color;
      return {
        id: booking.id,
        title: `📹 ${booking.title}`,
        start: booking.scheduled_at,
        end: endDate.toISOString(),
        backgroundColor: eventColor || 'hsl(25, 95%, 53%)',
        borderColor: eventColor || 'hsl(25, 95%, 43%)',
        textColor: 'white',
        classNames: booking.completed_at ? ['fc-event-completed'] : [],
        extendedProps: {
          activity: { ...booking, type: 'meeting' },
          type: 'meeting',
          isCompleted: !!booking.completed_at,
          dealId: null,
          dealTitle: null,
          customerName: booking.customer_name,
          description: booking.description,
          conferencingLink: booking.conferencing_link || null,
          isStandaloneBooking: true,
          bookingCancelToken: (booking as any).cancel_token || null,
          bookingStatus: (booking as any).booking_status || 'confirmed',
        },
      };
    });
    return [...events, ...standaloneEvents];
  }, [events, standaloneBookings]);

  /** Handler para mudar preset do período (filtro lateral) — sincroniza calendarDate */
  const handlePresetChange = (preset: PeriodPreset) => {
    const dates = calculatePeriodDates(preset);
    setFilters(prev => ({
      ...prev,
      period: { preset, startDate: dates.startDate, endDate: dates.endDate },
    }));
    // Sincronização inversa: atualiza toolbar para refletir o período do filtro
    setCalendarDate(dates.startDate);
  };

  /** Handler para período customizado (filtro lateral) — sincroniza calendarDate */
  const handleCustomChange = (startDate: Date, endDate: Date) => {
    setFilters(prev => ({
      ...prev,
      period: { preset: 'custom', startDate, endDate },
    }));
    // Sincronização inversa: toolbar aponta para início do período customizado
    setCalendarDate(startDate);
  };

  // Handler para abrir negócio
  const handleOpenDeal = (dealId: string) => {
    setSelectedDealId(dealId);
    setIsDealModalOpen(true);
  };

  // Estado e handler do botão de refresh
  const [isRefreshing, setIsRefreshing] = useState(false);

  /** Recarrega todos os dados da página: KPIs, calendário, lista e standalone */
  const handleRefreshAll = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchActivities(),
        refetchList(),
        refetchKpi(),
        refetchStandalone(),
      ]);
      showToast.success('Dados atualizados');
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchActivities, refetchList, refetchKpi, refetchStandalone]);

  /**
   * Handler para abrir modal de detalhes ao clicar em item da lista
   * Transforma o item da lista no formato esperado pelo CalendarEventModal
   */
  const handleOpenListActivity = useCallback((activity: any) => {
    setSelectedListEvent({
      activity: {
        id: activity.id,
        title: activity.title,
        description: activity.description,
        type: activity.type,
        scheduled_at: activity.scheduled_at,
        completed_at: activity.completed_at,
      },
      type: activity.type,
      isCompleted: !!activity.completed_at,
      dealId: activity.deal_id,
      dealTitle: activity.deal_title,
      customerName: activity.customer_name,
      description: activity.description,
      conferencingLink: activity.conferencing_link || null,
      // Campos de booking enriquecidos (v1.1.0)
      bookingCancelToken: activity.booking_cancel_token || activity.cancel_token || null,
      bookingStatus: activity.booking_status || null,
      hostUserName: activity.host_user_name || null,
      hostAvatarUrl: activity.host_avatar_url || null,
      calendarEmail: activity.calendar_email || null,
      calendarColor: activity.calendar_color || null,
    });
    setIsListEventModalOpen(true);
  }, []);

  /**
   * Handler para cancelar booking na visualização lista
   * Atualiza selectedListEvent localmente para feedback imediato
   */
  const handleListCancelBooking = useCallback((cancelToken: string, reason?: string) => {
    cancelActivity.mutate({ cancelToken, reason }, {
      onSuccess: () => {
        // Atualiza estado local do modal para refletir cancelamento imediato
        setSelectedListEvent((prev: any) => {
          if (!prev) return prev;
          const desc = prev.description || '';
          const prefixedDesc = desc.startsWith('❌ CANCELADO') ? desc : `❌ CANCELADO\n${desc}`;
          return {
            ...prev,
            isCompleted: true,
            bookingStatus: 'cancelled',
            bookingCancelToken: null,
            description: prefixedDesc,
            activity: {
              ...prev.activity,
              completed_at: new Date().toISOString(),
              description: prefixedDesc,
            },
          };
        });
        toast.success('Agendamento cancelado com sucesso');
      },
      onError: (error: Error) => {
        toast.error('Erro ao cancelar agendamento', {
          description: error.message || 'Tente novamente mais tarde',
        });
      },
    });
  }, [cancelActivity]);

  /**
   * Handler para reabrir atividade na visualização lista
   * Atualiza selectedListEvent localmente para feedback imediato
   */
  const handleListReopen = useCallback((activityId: string) => {
    reopenActivity.mutate(activityId, {
      onSuccess: () => {
        setSelectedListEvent((prev: any) => {
          if (!prev) return prev;
          const desc = prev.description || '';
          const cleanDesc = desc.startsWith('❌ CANCELADO')
            ? desc.replace(/^❌ CANCELADO\n?/, '')
            : desc;
          return {
            ...prev,
            isCompleted: false,
            bookingStatus: 'confirmed',
            description: cleanDesc,
            activity: {
              ...prev.activity,
              completed_at: null,
              description: cleanDesc,
            },
          };
        });
        toast.success('Atividade reaberta!');
      },
      onError: (error: Error) => {
        if (error.message === 'SLOT_CONFLICT') {
          toast.error('Horário indisponível', {
            description: 'Este horário já está ocupado por outro agendamento.',
          });
        } else {
          toast.error('Erro ao reabrir atividade');
        }
      },
    });
  }, [reopenActivity]);

  /**
   * Handler para concluir atividade na visualização lista
   * Atualiza selectedListEvent localmente para feedback imediato
   */
  const handleListComplete = useCallback((activityId: string) => {
    completeActivity.mutate(activityId, {
      onSuccess: () => {
        setSelectedListEvent((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            isCompleted: true,
            activity: {
              ...prev.activity,
              completed_at: new Date().toISOString(),
            },
          };
        });
        toast.success('Atividade concluída!');
      },
      onError: () => {
        toast.error('Erro ao concluir atividade');
      },
    });
  }, [completeActivity]);

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Cabeçalho: Título + Refresh | Filtros (scroll) | Toggle */}
      <div className="flex items-center gap-2">
        {/* Esquerda: Título + Refresh + Novo Agendamento (não encolhe) */}
        <div className="flex items-center gap-1.5 shrink-0">
          <h1 className="text-lg md:text-2xl font-bold tracking-tight">Agendamento</h1>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleRefreshAll}
            disabled={isRefreshing}
            className="h-7 w-7"
            title="Atualizar dados"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isRefreshing && "animate-spin")} />
          </Button>
        </div>

        {/* Separador visual */}
        <span className="text-muted-foreground/30 shrink-0">|</span>

        {/* Centro: Filtros com scroll horizontal */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none min-w-0 flex-1">
          <ActivitiesFilters filters={filters} onFiltersChange={setFilters} showSchedulingTypeFilter />
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="h-[22px] px-1.5 text-xs text-muted-foreground hover:text-foreground shrink-0"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

      </div>

      {/* Toggle Lista/Calendário + Botão Novo Agendamento - mesma linha */}
      <div className="flex items-center justify-between">
        <ToggleGroup
          type="single"
          value={viewMode}
          onValueChange={handleViewModeChange}
          size="sm"
          className="justify-start"
        >
          <ToggleGroupItem value="list" aria-label="Visualizar em lista" className="text-xs px-2.5 h-7 gap-1">
            <List className="h-3.5 w-3.5" />
            Lista
          </ToggleGroupItem>
          <ToggleGroupItem value="calendar" aria-label="Visualizar em calendário" className="text-xs px-2.5 h-7 gap-1">
            <CalendarDays className="h-3.5 w-3.5" />
            Calendário
          </ToggleGroupItem>
        </ToggleGroup>

        {/* Botão Novo Agendamento - alinhado à direita */}
        <Button
          size="sm"
          onClick={() => setIsInternalBookingOpen(true)}
          className="h-7 gap-1 text-xs"
        >
          <Plus className="h-3.5 w-3.5" />
          Novo Agendamento
        </Button>
      </div>

      {/* Conteúdo principal: alterna entre Calendário e Lista */}
      {viewMode === 'calendar' ? (
        /* Grid: Calendário 3/4 + Sidebar 1/4 */
        <div className="grid gap-3 sm:gap-4 lg:grid-cols-4 items-start">
          {/*
            Calendário Principal - full bleed no mobile.
            Em telas lg+, a altura é espelhada da sidebar (ver sidebarRef/ResizeObserver)
            para garantir que o FullCalendar nunca extrapole as bordas do card.
          */}
          <div
            className="lg:col-span-3 border-0 bg-transparent p-0 sm:bg-card sm:rounded-lg sm:border sm:p-4 flex flex-col"
            style={sidebarHeight ? { height: sidebarHeight } : undefined}
          >
            {/* Toolbar de navegação do calendário — dentro da div do calendário para alinhar ao seu limite */}
            <CalendarToolbar
              title={calendarTitle.charAt(0).toUpperCase() + calendarTitle.slice(1)}
              currentDate={calendarDate}
              currentView={calendarView}
              onPrev={handleCalendarPrev}
              onNext={handleCalendarNext}
              onToday={handleCalendarToday}
              onViewChange={handleCalendarViewChange}
            />
            {/* Wrapper flex-1 + min-h-0: permite ao FullCalendar (height="100%") ocupar
                exatamente o espaço restante após a toolbar, sem transbordar. */}
            <div className="flex-1 min-h-0">
              <ActivitiesFullCalendar
                onOpenDeal={handleOpenDeal}
                initialDate={new Date()}
                workspaceId={filters.workspaceId}
                sellerId={filters.sellerId}
                performedByUserId={filters.performedByUserId}
                status={filters.status}
                eventTypeId={filters.eventTypeId ?? null}
                additionalEvents={mergedCalendarEvents}
                externalDate={calendarDate}
                externalView={calendarView}
                onExternalDateChange={setCalendarDate}
                onExternalViewChange={setCalendarView}
                hideToolbar
              />
            </div>
          </div>

          {/* Sidebar: KPIs + Breakdown + Event Types - escondida no mobile.
              É a referência de altura para o card do calendário (sidebarRef). */}
          <div
            ref={sidebarRef}
            className="hidden lg:flex flex-col gap-4 lg:sticky lg:top-4 self-start"
          >
            <SchedulingKpiCard
              totalScheduled={kpiData.totalScheduled}
              overdueCount={kpiData.overdueCount}
              upcomingCount={kpiData.upcomingCount}
              completedCount={kpiData.completedCount}
              completionPercentage={kpiData.completionPercentage}
              isLoading={isLoadingKpi}
              avatarUrl={profile?.avatar_url}
              userName={profile?.full_name}
              periodFilter={periodFilter}
              onPresetChange={handlePresetChange}
              onCustomChange={handleCustomChange}
            />
            <SchedulingTypeBreakdownCard
              onlineCount={kpiData.onlineCount}
              meetingCount={kpiData.meetingCount}
              taskCount={kpiData.taskCount}
              isLoading={isLoadingKpi}
            />
            <SchedulingEventTypesCompact />
          </div>
        </div>
      ) : (
        /* Visualização em Lista + Sidebar */
        <div className="grid gap-4 lg:grid-cols-4" style={{ minHeight: 'calc(100vh - 160px)' }}>
          <div className="lg:col-span-3 flex flex-col">
            {/* Toolbar de navegação — também na lista para manter consistência */}
            <CalendarToolbar
              title={calendarTitle.charAt(0).toUpperCase() + calendarTitle.slice(1)}
              currentDate={calendarDate}
              currentView={calendarView}
              onPrev={handleCalendarPrev}
              onNext={handleCalendarNext}
              onToday={handleCalendarToday}
              onViewChange={handleCalendarViewChange}
            />
            <SchedulingListView
              activities={mergedListActivities}
              isLoading={isLoadingList || isLoadingStandalone}
              onOpenDeal={handleOpenDeal}
              onOpenActivity={handleOpenListActivity}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLinkToDeal={(bookingId, guestName) => {
                setLinkBookingId(bookingId);
                setLinkGuestName(guestName);
                setIsLinkDialogOpen(true);
              }}
            />
          </div>

          {/* Modal de detalhes da atividade (visualização lista) */}
          <CalendarEventModal
            open={isListEventModalOpen}
            onOpenChange={(open) => {
              setIsListEventModalOpen(open);
              if (!open) setSelectedListEvent(null);
            }}
            event={selectedListEvent}
            onComplete={handleListComplete}
            onReopen={handleListReopen}
            onOpenDeal={handleOpenDeal}
            onCancelBooking={handleListCancelBooking}
            isCompleting={completeActivity.isPending}
            isReopening={reopenActivity.isPending}
            isCancelling={cancelActivity.isPending}
          />

          {/* Sidebar (mesma do calendário) */}
          <div className="flex flex-col gap-4 lg:sticky lg:top-4 self-start">
            <SchedulingKpiCard
              totalScheduled={kpiData.totalScheduled}
              overdueCount={kpiData.overdueCount}
              upcomingCount={kpiData.upcomingCount}
              completedCount={kpiData.completedCount}
              completionPercentage={kpiData.completionPercentage}
              isLoading={isLoadingKpi}
              avatarUrl={profile?.avatar_url}
              userName={profile?.full_name}
              periodFilter={periodFilter}
              onPresetChange={handlePresetChange}
              onCustomChange={handleCustomChange}
            />
            <SchedulingTypeBreakdownCard
              onlineCount={kpiData.onlineCount}
              meetingCount={kpiData.meetingCount}
              taskCount={kpiData.taskCount}
              isLoading={isLoadingKpi}
            />
            <SchedulingEventTypesCompact />
          </div>
        </div>
      )}

      {/* Modal de detalhes do negócio */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={isDealModalOpen}
        onOpenChange={(open) => {
          setIsDealModalOpen(open);
          if (!open) setSelectedDealId(null);
        }}
      />

      {/* Dialog para vincular booking standalone a um deal */}
      <LinkBookingToDealDialog
        open={isLinkDialogOpen}
        onOpenChange={setIsLinkDialogOpen}
        bookingId={linkBookingId}
        guestName={linkGuestName}
        onLinked={(bookingId, dealId) => {
          linkToDeal.mutate({ bookingId, dealId }, {
            onSuccess: () => {
              showToast.success('Agendamento vinculado ao negócio com sucesso');
              setIsLinkDialogOpen(false);
            },
            onError: () => {
              showToast.error('Erro ao vincular agendamento');
            },
          });
        }}
        isLinking={linkToDeal.isPending}
      />

      {/* Dialog de novo agendamento interno */}
      <InternalBookingDialog
        open={isInternalBookingOpen}
        onOpenChange={setIsInternalBookingOpen}
      />
    </div>
  );
}
